import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import * as _ from 'lodash';
import { environment } from 'src/environments/environment';

import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';

import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { Orientation } from 'src/app/api/services/presentation';
import { BlueprintService } from 'src/app/template-editor/services/blueprint.service';
import {
  Blueprint,
  BlueprintComponent,
} from 'src/app/presentations/services/blueprint-generator.service';
import { ComponentsService } from 'src/app/template-editor/services/components.service';
import { StorageCacheBusterService } from 'src/app/template-editor/services/storage-cache-buster.service';
import { TemplateEditorService } from 'src/app/template-editor/services/template-editor.service';
import { BrandingService } from 'src/app/template-editor/template-components/services/branding.service';

@Component({
  selector: 'presentation-preview',
  templateUrl: './presentation-preview.component.html',
  styleUrls: ['./presentation-preview.component.scss'],
  standalone: false,
})
export class PresentationPreviewComponent
  implements AfterViewInit, DoCheck, OnDestroy, OnInit, OnChanges
{
  @Input() disableAutoRefresh = false;
  @Input() templatePreviewHtml?: string;
  @Input() disableInteraction = false;
  @Input() showBranding = false;
  @Input() orientation?: Orientation;

  private DEFAULT_TEMPLATE_WIDTH = 1920;
  private DEFAULT_TEMPLATE_HEIGHT = 1080;
  private PREVIEW_SHADOW_INSET_PX = 2;
  private MOBILE_PREVIEW_HEIGHT = 210;
  private MOBILE_PREVIEW_HEIGHT_SHORT = 150;
  private PREVIEW_INITIAL_DELAY_MILLIS = 1000;
  private RLC_STAGING_PARAM = 'rlcStage';
  private CT_STAGING_PARAM = 'ctStage';
  private AI_WRAPPER_PC = '8fad9b4ba40a149e29a6c607a24920259d515917';

  private subscription: any;

  private iframeLoaded = false;
  private localBlueprintData: Blueprint;
  private debounceAttributeDataTimer;
  private frameElementId = crypto.randomUUID();
  public editorPreviewUrl;

  private componentWasSelected = false;

  private debounceResizeTimer;

  @ViewChild('templateEditorPreviewParent') iframeParent;
  @ViewChild('templateEditorPreview') iframe;

  private _getActivePresentation() {
    return this.templateEditorFactory?.presentation || {};
  }

  private _getActiveBlueprintData() {
    return (
      this.localBlueprintData || this.blueprintFactory?.blueprintData || {}
    );
  }

  private _getActiveBrandingSettings() {
    return this.brandingFactory?.brandingSettings || {};
  }

  private _getLogoComponents() {
    const blueprintData = this._getActiveBlueprintData();
    if (!blueprintData || !blueprintData.components) {
      return [];
    }

    return this.blueprintFactory.getLogoComponents(blueprintData);
  }

  _getPreviewHolder() {
    return this.elementRef.nativeElement;
  }

  _getIframeParent() {
    return this.iframeParent.nativeElement;
  }

  _getIframe(): any {
    return this.iframe.nativeElement;
  }

  onResize() {
    if (this.debounceResizeTimer) {
      clearTimeout(this.debounceResizeTimer);
    }

    this.debounceResizeTimer = setTimeout(() => {
      this._applyAspectRatio();
    }, 100);
  }

  constructor(
    private elementRef: ElementRef,
    private sanitizer: DomSanitizer,
    private broadcaster: BroadcasterService,
    private companyStateService: CompanyStateService,
    private templateEditorFactory: TemplateEditorService,
    private componentsFactory: ComponentsService,
    private storageCacheBusterService: StorageCacheBusterService,
    private blueprintFactory: BlueprintService,
    private brandingFactory: BrandingService,
  ) {}

  ngOnInit(): void {
    if (!this.disableAutoRefresh) {
      this.subscription = this.broadcaster.subscribe((event: string) => {
        switch (event) {
          case 'presentationCreated':
          case 'risevision.template-editor.presentationLoaded':
            this._setEditorPreviewUrl();
            break;
          case 'risevision.template-editor.historyRestored':
            this._postAttributeData();
            this._applyAspectRatio();
            this.componentsFactory.highlightComponent(
              this.componentsFactory.selected?.id || null,
            );
            break;
          // A component's box changed programmatically (e.g. the hugging-box
          // computation for a newly-imported Canva image) after it was
          // already selected. The selection overlay only ever re-measures on
          // an explicit highlight, so — like historyRestored above — push
          // immediately (not debounced) and re-highlight the current
          // selection so the overlay picks up the new geometry.
          case 'risevision.template-editor.componentBoxUpdated':
            this._postAttributeData();
            this.componentsFactory.highlightComponent(
              this.componentsFactory.selected?.id || null,
            );
            break;
          case 'risevision.template-editor.presentationUnsavedChanges':
          case 'risevision.template-editor.logoFileMetadata':
          case 'risevision.template-editor.storageCacheLoaded':
            this._debounceAttributeData(this._postAttributeData);
            break;
          case 'risevision.template-editor.presentationOrientationChanged':
            if (!this.templatePreviewHtml) {
              this._applyAspectRatio();
            }
            break;
          case 'risevision.company.updated':
          case 'risevision.company.selectedCompanyChanged':
          case 'risevision.template-editor.brandingUnsavedChanges':
            setTimeout(() => {
              this._postDisplayData();
            });
            break;
          default:
            return;
        }
      });
    }

    this._setEditorPreviewUrl();
  }

  ngAfterViewInit() {
    this._applyAspectRatio();
    setTimeout(
      this._applyAspectRatio.bind(this),
      this.PREVIEW_INITIAL_DELAY_MILLIS,
    );
  }

  ngDoCheck(): void {
    if (this.componentWasSelected && !this.componentsFactory.selected) {
      this._applyAspectRatio();
    }

    this.componentWasSelected = !!this.componentsFactory.selected;
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onLoad(event) {
    if (event.target.src != '' || event.target.srcdoc != '') {
      // The wrapper rewrites its own document, which fires load again
      if (this.iframeLoaded) {
        return;
      }

      this.iframeLoaded = true;

      if (this.templatePreviewHtml) {
        this._initializeWithPreviewHtml();
      } else if (this._isAiGeneratedTemplate()) {
        this._initializeWrappedTemplate();
      } else {
        this.initializeTemplate();
      }
    }
  }

  protected initializeTemplate(attributeData?) {
    this._postDisplayData();
    this._postAttributeData(attributeData);
    this._postStartEvent();
  }

  @HostListener('window:message', ['$event'])
  async _onWindowMessage(event: MessageEvent) {
    const data: any = event.data;

    if (
      data?.frameElementId !== this.frameElementId ||
      data?.topic !== 'get-template-data'
    ) {
      return;
    }

    this.initializeTemplate(await this._computeAttributeData());
  }

  private async _computeAttributeData(): Promise<any> {
    // Standalone Previews ignore shared editor data to ensure fresh results
    const presentation = this.templatePreviewHtml
      ? null
      : this._getActivePresentation();
    const templateHtml = this.templatePreviewHtml || presentation?.layout;
    const orientation = this.templatePreviewHtml
      ? this.orientation
      : (presentation?.templateAttributeData?.orientation as
          | Orientation
          | undefined);

    const blueprint = await this._generateBlueprint(
      templateHtml,
      true,
      orientation,
    );

    const hasSavedAttributeData =
      presentation?.templateAttributeData?.components?.length > 0;

    return hasSavedAttributeData
      ? undefined
      : await this.blueprintFactory.buildAttributeData(blueprint);
  }

  protected _initializeWrappedTemplate() {
    const presentation = this._getActivePresentation();

    if (!presentation || !presentation.layout) {
      console.warn('No presentation layout found for AI generated template');
      return;
    }

    this._sendTemplateContentToWrapper(presentation.layout);
  }

  private _initializeWithPreviewHtml(): void {
    if (!this.templatePreviewHtml) {
      console.warn('No template preview HTML provided for initialization');
      return;
    }

    this._sendTemplateContentToWrapper(this.templatePreviewHtml);
  }

  private async _generateBlueprint(
    templateHtml: string,
    useBranding: boolean = true,
    orientation?: Orientation,
  ): Promise<Blueprint> {
    const blueprint =
      await this.blueprintFactory.generateBlueprintDataFromLayout(
        templateHtml,
        useBranding,
        orientation,
      );

    this.localBlueprintData = blueprint;
    return blueprint;
  }

  private _sendTemplateContentToWrapper(layout: string): void {
    try {
      this._postMessageToTemplate({ type: 'html', html: layout });
    } catch (err) {
      console.error('Failed to send template content to wrapper', err);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['templatePreviewHtml'] &&
      !changes['templatePreviewHtml'].firstChange
    ) {
      if (this.iframeLoaded && this.templatePreviewHtml) {
        this.reloadIframe();
      }
    }

    if (changes['showBranding'] && !changes['showBranding'].firstChange) {
      if (this.iframeLoaded && this.templatePreviewHtml) {
        this.reloadIframe();
      }
    }
  }

  private reloadIframe(): void {
    this.iframeLoaded = false;
    const iframe = this._getIframe();
    if (iframe) {
      iframe.src = iframe.src;
    }
  }

  _debounceAttributeData(callback) {
    if (this.debounceAttributeDataTimer) {
      clearTimeout(this.debounceAttributeDataTimer);
    }

    this.debounceAttributeDataTimer = setTimeout(callback.bind(this), 500);
  }

  _getWindowHref() {
    return window.location.href;
  }

  _getContentStagingParams() {
    let componentsStage = '';
    let commonStage = '';
    const parsedUrl = new URL(this._getWindowHref());

    parsedUrl.searchParams.forEach((value, key) => {
      if (key === this.RLC_STAGING_PARAM) {
        componentsStage = '&' + this.RLC_STAGING_PARAM + '=' + value;
      }

      if (key === this.CT_STAGING_PARAM) {
        commonStage = '&' + this.CT_STAGING_PARAM + '=' + value;
      }
    });

    return componentsStage + commonStage;
  }

  _isAiGeneratedTemplate() {
    return !!this._getActivePresentation().layout;
  }

  _setEditorPreviewUrl() {
    if (this.templatePreviewHtml) {
      const companyId = this.companyStateService.getSelectedCompanyId();
      const contentStagingParams = this._getContentStagingParams();
      const url =
        environment.HTML_TEMPLATE_URL.replace(
          'PRODUCT_CODE',
          this.AI_WRAPPER_PC,
        ) +
        '?type=preview&cid=' +
        companyId +
        '&frameElementId=' +
        this.frameElementId +
        contentStagingParams;
      this._applyPreviewUrl(url);
    } else {
      // Template Editor: Use presentation object
      const presentation = this._getActivePresentation();
      if (!presentation) {
        return;
      }

      const productCode = this._isAiGeneratedTemplate()
        ? this.AI_WRAPPER_PC
        : presentation.productCode;
      const presentationId = presentation.id;
      const companyId = presentation.companyId;

      if (!productCode || !presentationId || !companyId) {
        return;
      }

      const contentStagingParams = this._getContentStagingParams();

      const url =
        environment.HTML_TEMPLATE_URL.replace('PRODUCT_CODE', productCode) +
        '?type=preview&cid=' +
        companyId +
        '&presentationId=' +
        presentationId +
        (this._isAiGeneratedTemplate()
          ? '&frameElementId=' + this.frameElementId
          : '') +
        contentStagingParams;

      this._applyPreviewUrl(url);
    }
  }

  private _applyPreviewUrl(url: string): void {
    // A new url navigates the iframe, so the current document is gone
    this.iframeLoaded = false;
    this.editorPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  _getPresetDimensions() {
    const orientationPreset =
      this._getActivePresentation()?.templateAttributeData?.orientationPreset;

    // orientationPreset is set only if the template is responsive.
    // Allowed values:
    // undefined or null - same as'responsive'. Editor should use default template dimensions from blueprint.
    // 'landscape' - this is the default orientation for responsive templates. Editor should use default template dimensions from blueprint.
    // 'portrait' - to show responsive templates in portrait orientation, we need to switch width and height.
    // 'custom' - user has entered custom dimensions.

    if (orientationPreset === 'portrait' || orientationPreset === 'custom') {
      const attreData = this._getActivePresentation()?.templateAttributeData;
      const width = attreData?.widthPreset;
      const height = attreData?.heightPreset;

      if (width && height) {
        return { width, height };
      }
    }
    return null;
  }

  _getTemplateWidth() {
    const presetDimentions = this._getPresetDimensions();

    if (presetDimentions) {
      return presetDimentions.width;
    }

    const blueprintData = this._getActiveBlueprintData();
    const width = blueprintData && blueprintData.width;

    return width ? parseInt(width) : this.DEFAULT_TEMPLATE_WIDTH;
  }

  _getTemplateHeight() {
    const presetDimentions = this._getPresetDimensions();

    if (presetDimentions) {
      return presetDimentions.height;
    }

    const blueprintData = this._getActiveBlueprintData();
    const height = blueprintData && blueprintData.height;

    return height ? parseInt(height) : this.DEFAULT_TEMPLATE_HEIGHT;
  }

  _getPreviewAreaWidth() {
    const previewHolder = this._getPreviewHolder();
    return Math.max(
      0,
      previewHolder.clientWidth - this.PREVIEW_SHADOW_INSET_PX,
    );
  }

  _getPreviewAreaHeight() {
    const previewHolder = this._getPreviewHolder();
    return Math.max(
      0,
      previewHolder.clientHeight - this.PREVIEW_SHADOW_INSET_PX,
    );
  }

  _getHeightDividedByWidth() {
    return this._getTemplateHeight() / this._getTemplateWidth();
  }

  _useFullWidth() {
    const aspectRatio = this._getHeightDividedByWidth();
    const projectedHeight = this._getPreviewAreaWidth() * aspectRatio;

    return projectedHeight < this._getPreviewAreaHeight();
  }

  _getWidthFor(height) {
    const value = height / this._getHeightDividedByWidth();

    return value;
  }

  _mediaMatches(mediaQuery) {
    return window.matchMedia(mediaQuery).matches;
  }

  getMobileWidth() {
    const value = this._getWidthFor(this.getMobileHeight());

    return value.toFixed(0);
  }

  getMobileHeight() {
    const isShort = this._mediaMatches('(max-height: 570px)');
    return isShort
      ? this.MOBILE_PREVIEW_HEIGHT_SHORT
      : this.MOBILE_PREVIEW_HEIGHT;
  }

  getDesktopWidth() {
    const height = this._getPreviewAreaHeight();

    return this._getWidthFor(height).toFixed(0);
  }

  _getFrameStyle(viewSize, templateSize) {
    const ratio = (viewSize / templateSize).toFixed(4);
    const width = this._getTemplateWidth();
    const height = this._getTemplateHeight();

    return (
      'width: ' +
      width +
      'px;' +
      'height: ' +
      height +
      'px;' +
      'transform:scale3d(' +
      ratio +
      ',' +
      ratio +
      ',' +
      ratio +
      ');'
    );
  }

  _applyAspectRatio() {
    let frameStyle;
    let parentStyle = 'height: 100%; width: 100%;';
    const isMobile = this._mediaMatches('(max-width: 768px)');

    const iframeParent = this._getIframeParent();
    const iframe = this._getIframe();

    if (isMobile) {
      const viewHeight = this.getMobileHeight();
      parentStyle =
        'height: ' + viewHeight + 'px; width: ' + this.getMobileWidth() + 'px;';
      frameStyle = this._getFrameStyle(viewHeight, this._getTemplateHeight());
    } else if (this._useFullWidth()) {
      const viewWidth = this._getPreviewAreaWidth();
      frameStyle = this._getFrameStyle(viewWidth, this._getTemplateWidth());
    } else {
      const viewHeight = this._getPreviewAreaHeight();
      frameStyle = this._getFrameStyle(viewHeight, this._getTemplateHeight());
    }

    iframeParent.setAttribute('style', parentStyle);
    iframe.setAttribute('style', frameStyle);
  }

  _updateLogoData(attributeData) {
    if (attributeData && attributeData.components) {
      const logoComponents = this._getLogoComponents();
      const brandingSettings = this._getActiveBrandingSettings();

      _.each(logoComponents, (logoComponent) => {
        const component = _.find(attributeData.components, {
          id: logoComponent.id,
        });

        if (component && component.isLogo !== false && brandingSettings) {
          component.metadata = brandingSettings.logoFileMetadata;
        }
      });
    }
  }

  // AI layouts position components inline, so an entry without customization
  // makes the template reset the component to no position
  _fillMissingCustomization(attributeData) {
    if (
      (!this._isAiGeneratedTemplate() && !this.templatePreviewHtml) ||
      !attributeData?.components
    ) {
      return;
    }

    const blueprintComponents: BlueprintComponent[] =
      this._getActiveBlueprintData().components || [];

    attributeData.components.forEach((component) => {
      if (Object.keys(component.customization || {}).length) {
        return;
      }

      const position = blueprintComponents.find(
        (blueprintComponent) => blueprintComponent.id === component.id,
      )?.position;

      if (position) {
        component.customization = { ...position };
      }
    });
  }

  getAttributeData() {
    return _.cloneDeep(
      this.templateEditorFactory.presentation.templateAttributeData,
    );
  }

  postAttributeData(newData?) {
    const presentation = this._getActivePresentation();

    if (!newData && (!presentation || !presentation.templateAttributeData)) {
      return;
    }

    const attributeData =
      newData || _.cloneDeep(presentation.templateAttributeData);

    this._fillMissingCustomization(attributeData);

    this._updateLogoData(attributeData);

    this.storageCacheBusterService.updateStorageFileTimestamps(attributeData);

    const message = {
      type: 'attributeData',
      value: attributeData,
    };
    this._postMessageToTemplate(message);

    // Components the preview creates from this data, like one just added, or all of them when
    // the preview loads, missed the selection sent when it changed, so it's sent again after it
    this._postMessageToTemplate({
      topic: ComponentsService.SELECTED_TOPIC,
      componentId: this.componentsFactory.selected?.id || null,
    });
  }

  _postAttributeData(attributeData?) {
    this.postAttributeData(attributeData);
  }

  _postStartEvent() {
    this._postMessageToTemplate({
      type: 'sendStartEvent',
    });
  }

  _postDisplayData() {
    const company = this.companyStateService.getCopyOfSelectedCompany(true);
    const brandingSettings =
      !this.templatePreviewHtml || this.showBranding
        ? this._getActiveBrandingSettings()
        : null;

    const message = {
      type: 'displayData',
      value: {
        displayAddress: {
          city: company.city,
          province: company.province,
          country: company.country,
          postalCode: company.postalCode,
        },
        companyBranding: brandingSettings
          ? {
              logoFile: brandingSettings.logoFile,
              baseColor: brandingSettings.baseColor,
              accentColor: brandingSettings.accentColor,
            }
          : {},
      },
    };
    this._postMessageToTemplate(message);
  }

  _postMessageToTemplate(message) {
    if (!this.iframeLoaded) {
      return;
    }

    let iframe = this._getIframe();

    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        JSON.stringify(message),
        TemplateEditorService.HTML_TEMPLATE_DOMAIN,
      );
    } else {
      setTimeout(() => {
        this._postMessageToTemplate(message);
      }, 1000);
    }
  }
}
